import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { Link } from "react-router-dom";
import { BANNER_SLIDES, ROUTES } from "../constants";

// TODO: Replace with production motorcycle photography
// Current: development placeholders from assest/banner/
// To swap images, update this array — the component structure stays unchanged.
import image1 from "../assest/banner/1.png";
import image2 from "../assest/banner/2.png";
import image3 from "../assest/banner/3.png";
import image4 from "../assest/banner/4.png";
import image5 from "../assest/banner/5.png";

const HERO_IMAGES = [image1, image2, image3, image4, image5];

const BannerProduct = () => {
  const [currentImage, setCurrentImage] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const nextImage = useCallback(
    () => setCurrentImage((prev) => (prev + 1) % HERO_IMAGES.length),
    [],
  );
  const prevImage = () =>
    setCurrentImage(
      (prev) => (prev - 1 + HERO_IMAGES.length) % HERO_IMAGES.length,
    );

  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(nextImage, 6000);
    return () => clearInterval(interval);
  }, [nextImage, isAutoPlaying]);

  const slide = BANNER_SLIDES[currentImage];

  return (
    <div
      className="relative w-full h-[calc(100vh-64px)] min-h-[600px] bg-ds-inverse-surface overflow-hidden flex items-end pb-[100px]"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
      aria-label="Hero banner"
    >
      {/* ── Background image with Ken Burns effect ── */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentImage}
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="absolute inset-0"
        >
          {/* TODO: Replace with production motorcycle photography
              Current: development placeholder from assest/banner/ */}
          <img
            src={HERO_IMAGES[currentImage]}
            alt={slide.title}
            className="w-full h-full object-cover object-center mix-blend-multiply opacity-50"
            loading={currentImage === 0 ? "eager" : "lazy"}
          />
        </motion.div>
      </AnimatePresence>

      {/* ── Gradient overlay (bottom-weighted, matches index.html) ── */}
      <div
        className="absolute inset-0 z-10"
        style={{
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.78) 100%)",
        }}
        aria-hidden="true"
      />

      {/* ── Hero content ── */}
      <div className="relative z-20 w-full max-w-[1280px] mx-auto px-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={`content-${currentImage}`}
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 40 }}
            transition={{ delay: 0.2, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-3xl"
          >
            <h1 className="font-barlow font-black text-[64px] md:text-[88px] leading-[0.9] text-white mb-4 uppercase tracking-tighter">
              EVERYTHING YOUR RIDE NEEDS
            </h1>

            <p className="font-inter text-[18px] text-white/80 mb-8 max-w-xl leading-[1.6]">
              {slide.subtitle} — helmets, gloves, oils, accessories and more.
              All brands, all budgets.
            </p>

            <div className="flex flex-wrap gap-4">
              <Link
                to={ROUTES.PRODUCT_CATEGORY}
                className="inline-flex items-center justify-center px-8 py-4 bg-ds-primary hover:bg-ds-primary-hover text-white font-inter font-semibold text-[16px] rounded-full transition-all duration-300"
                aria-label="Shop all products"
              >
                SHOP ALL PRODUCTS
              </Link>
              <Link
                to={ROUTES.HOT_SALE}
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-inter font-semibold text-[16px] rounded-full hover:bg-white hover:text-ds-on-background transition-colors duration-300"
                aria-label="View sale deals"
              >
                VIEW DEALS %
              </Link>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* ── Slide navigation arrows ── */}
      <div className="absolute inset-y-0 left-4 md:left-6 flex items-center z-20">
        <button
          onClick={prevImage}
          aria-label="Previous slide"
          className="w-10 h-10 bg-white/10 hover:bg-white/90 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:text-ds-on-background transition-all duration-300 border border-white/20"
        >
          <FaChevronLeft className="text-sm" />
        </button>
      </div>
      <div className="absolute inset-y-0 right-4 md:right-6 flex items-center z-20">
        <button
          onClick={nextImage}
          aria-label="Next slide"
          className="w-10 h-10 bg-white/10 hover:bg-white/90 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:text-ds-on-background transition-all duration-300 border border-white/20"
        >
          <FaChevronRight className="text-sm" />
        </button>
      </div>

      {/* ── Dot indicators ── */}
      <div className="absolute bottom-8 left-10 flex items-center gap-2 z-20">
        {HERO_IMAGES.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrentImage(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`transition-all duration-500 rounded-full ${
              currentImage === i
                ? "w-8 h-2 bg-white"
                : "w-2 h-2 bg-white/35 hover:bg-white/60"
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default BannerProduct;
